import { z } from 'zod';
import type { UseFormReturn } from 'react-hook-form';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import type { PrincipalIdentifierLocal } from '../../api/types/PlatformLocal';
import { PHONE_E164_REGEX } from '../../lib/identifier';

/**
 * DEC-RBAC-079/081 — Módulo compartido del bloque "identificador de login" de los forms de alta de
 * usuario. Un tenant declara su `principalIdentifier` ('EMAIL' | 'PHONE'): con qué dato inician sesión
 * sus usuarios. Este módulo centraliza las TRES piezas para que cualquier form de alta
 * (platform `PlatformTenantUserForm`, tenant `ScopedUserForm`) las reuse sin duplicar:
 *   1. `buildIdentifierSchema` — el sub-schema zod (SÓLO el primario, requerido).
 *   2. `<IdentifierFields>` — el render (SÓLO el campo primario).
 *   3. `identifierBody` — arma la parte del body de creación ({ primario, primaryIdentifier }).
 *
 * REGLA DE NEGOCIO (Andres): el alta captura SÓLO el identificador PRIMARIO. El SECUNDARIO NO se setea
 * en el alta porque no se verifica: lo agrega el propio usuario después (self-service con OTP). Por eso
 * `allowSecondary` NO participa aquí — el módulo sólo conoce el primario.
 *
 * Objetivo DRY: el consumidor no decide "email vs teléfono" en ningún lado; le pasa
 * `principalIdentifier` (ej. de `useTenantIdentifier`) y las tres piezas se comportan solas.
 */

// Regex E.164 desde la fuente única compartida (misma que usa el login). Ver src/lib/identifier.ts.
const PHONE_MSG = 'Teléfono en formato +52... (10 a 15 dígitos)';

/** Shape del bloque identificador dentro de los `values` del form. Ambos campos siempre presentes en RHF. */
export interface IdentifierFormValues {
    email: string;
    phone: string;
}

/**
 * Sub-schema zod del bloque identificador según la config del tenant. SÓLO valida el PRIMARIO (el alta
 * no captura secundario). El campo opuesto queda `optional()` porque RHF lo maneja en `''` (no se
 * muestra ni se valida).
 *
 * - `principalIdentifier === 'EMAIL'`: `email` requerido (email válido); `phone` opcional (no se muestra).
 * - `principalIdentifier === 'PHONE'`: `phone` requerido (E.164); `email` opcional (no se muestra).
 *
 * Diseñado para mergear en el schema del form:
 *   `buildIdentifierSchema(pi).extend({ displayName, roleId, customFields })`.
 */
export function buildIdentifierSchema(
    principalIdentifier: PrincipalIdentifierLocal,
): z.ZodObject<{ email: z.ZodTypeAny; phone: z.ZodTypeAny }> {
    if (principalIdentifier === 'PHONE') {
        return z.object({
            phone: z.string().regex(PHONE_E164_REGEX, PHONE_MSG),
            email: z.string().optional(),
        });
    }
    // EMAIL primario (default / back-compat).
    return z.object({
        email: z.string().email('Email inválido'),
        phone: z.string().optional(),
    });
}

interface IdentifierFieldsProps {
    /**
     * El `form` de react-hook-form del form de usuario que embebe este bloque. Tipado a `UseFormReturn`
     * (FieldValues) para no acoplar el namespace al schema del caller; `email`/`phone` se direccionan por
     * string (igual que `DynamicUserFields`).
     */
    form: UseFormReturn;
    principalIdentifier: PrincipalIdentifierLocal;
    /** Prefijo de id para a11y (htmlFor/aria). Ej. `ptuf` → `ptuf-email` / `ptuf-phone`. */
    idPrefix: string;
}

/** Un input con su Label + error de zod, mismo patrón que los forms existentes del repo. */
function Field({
    form,
    name,
    id,
    label,
    type,
}: {
    form: UseFormReturn;
    name: 'email' | 'phone';
    id: string;
    label: string;
    type: string;
}) {
    const error = form.formState.errors[name]?.message as string | undefined;
    return (
        <div className="space-y-2">
            <Label htmlFor={id}>{label}</Label>
            <Input id={id} type={type} {...form.register(name)} />
            {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
    );
}

/**
 * Bloque de campos del identificador de login. Renderiza SÓLO el campo PRIMARIO (Teléfono si el tenant
 * es phone-primary, Email si es email-primary; requerido). El secundario NO se captura en el alta
 * (regla de negocio: lo agrega el usuario después con OTP).
 */
export function IdentifierFields({ form, principalIdentifier, idPrefix }: IdentifierFieldsProps) {
    if (principalIdentifier === 'PHONE') {
        return (
            <Field form={form} name="phone" id={`${idPrefix}-phone`} label="Teléfono" type="tel" />
        );
    }
    return <Field form={form} name="email" id={`${idPrefix}-email`} label="Email" type="email" />;
}

/**
 * Arma la parte "identificador" del body de creación de usuario a partir de los `values` del form.
 * SÓLO el PRIMARIO — el alta no captura secundario (el opuesto queda en `''` en RHF y se omite).
 *
 * - El campo PRIMARIO siempre va (trim).
 * - `primaryIdentifier` en minúscula (`'email'` | `'phone'`) — lo que espera el connector.
 */
export function identifierBody(
    values: IdentifierFormValues,
    principalIdentifier: PrincipalIdentifierLocal,
): { email?: string; phone?: string; primaryIdentifier: 'email' | 'phone' } {
    if (principalIdentifier === 'PHONE') {
        return {
            phone: values.phone.trim(),
            primaryIdentifier: 'phone',
        };
    }
    return {
        email: values.email.trim(),
        primaryIdentifier: 'email',
    };
}
